import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";

export interface CanvasSignatureProps {
  onChange: (dataUrl: string | null) => void;
  initialValue?: string | null;
  width?: number;
  height?: number;
  lineWidth?: number;
  lineColor?: string;
}

export const CanvasSignature: React.FC<CanvasSignatureProps> = ({
  onChange,
  initialValue,
  width = 500,
  height = 200,
  lineWidth = 2,
  lineColor = "#000"
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!initialValue);
  const [statusMessage, setStatusMessage] = useState<string>("");
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  // טען חתימה קיימת אם יש
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !initialValue) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctxRef.current = ctx;

    // דגל לביטול הטעינה אם הקומפוננטה unmount או initialValue משתנה
    let cancelled = false;

    // טען את החתימה
    const img = new Image();
    img.onload = () => {
      // בדוק אם הטעינה בוטלה (למשל, initialValue השתנה ל-null)
      if (cancelled) {
        return;
      }
      ctx.drawImage(img, 0, 0);
      setEmpty(false);
    };
    img.src = initialValue;

    // cleanup function - מבטל את הטעינה אם initialValue משתנה
    return () => {
      cancelled = true;
    };
  }, [initialValue]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctxRef.current = ctx;

    ctx.lineWidth = lineWidth;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = lineColor;

    const getPos = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      // @ts-ignore
      const clientX = (e.touches ? e.touches[0].clientX : (e as MouseEvent).clientX);
      // @ts-ignore
      const clientY = (e.touches ? e.touches[0].clientY : (e as MouseEvent).clientY);

      // חישוב ה-scale בין הגודל הפיזי של הקנבס לגודל המוצג
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY
      };
    };

    const start = (e: MouseEvent | TouchEvent) => {
      drawing.current = true;
      const { x, y } = getPos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      setStatusMessage("מתחיל לצייר חתימה");
      e.preventDefault();
    };
    const move = (e: MouseEvent | TouchEvent) => {
      if (!drawing.current) return;
      const { x, y } = getPos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      setEmpty(false);
      e.preventDefault();
    };
    const end = () => {
      drawing.current = false;
      // בדוק אם יש תוכן על ידי קריאה לקנבס, לא על ידי state
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const hasContent = imageData.data.some((channel) => channel !== 0);
      if (hasContent) {
        const dataUrl = canvas.toDataURL("image/png");
        onChange(dataUrl);
        setStatusMessage("חתימה הושלמה");
      }
    };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);

    canvas.addEventListener("touchstart", start, { passive: false } as any);
    canvas.addEventListener("touchmove", move, { passive: false } as any);
    window.addEventListener("touchend", end);

    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start as any);
      canvas.removeEventListener("touchmove", move as any);
      window.removeEventListener("touchend", end);
    };
  }, [onChange, lineWidth, lineColor]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    // שמור את הגדלים לפני הניקוי
    const { width: w, height: h } = canvas;

    // איפוס גודל - זה המקום היחיד שבאמת מנקה הכל!
    canvas.width = w;
    canvas.height = h;

    // קבל context חדש אחרי האיפוס
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctxRef.current = ctx;

    // אתחל את כל ההגדרות של ה-context
    ctx.lineWidth = lineWidth;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = lineColor;

    setEmpty(true);
    onChange(null);
    setStatusMessage("החתימה נוקתה");
  };

  return (
    <Box>
      <Box
        component="div"
        role="img"
        aria-label={empty ? "אזור חתימה ריק - השתמש בעכבר או במגע כדי לחתום" : "אזור חתימה עם חתימה"}
        sx={{ position: 'relative' }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{
            border: "2px solid #ccc",
            width: "100%",
            maxWidth: width,
            touchAction: "none",
            cursor: "crosshair"
          }}
          tabIndex={0}
          aria-describedby="signature-instructions"
          role="application"
          aria-label="אזור חתימה דיגיטלית"
        />
        <Typography
          id="signature-instructions"
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
        >
          {!empty && (
            <Button onClick={clear} size="small" sx={{ mr: 1, minWidth: 0, p: "2px 8px", fontSize: "inherit" }} aria-label="נקה את החתימה והתחל מחדש">
              נקה חתימה
            </Button>
          )}
          השתמש בעכבר או במגע כדי לצייר את החתימה שלך
        </Typography>
      </Box>

      {/* Live region for status updates */}
      <Box
        component="div"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {statusMessage}
      </Box>

    </Box>
  );
};

export default CanvasSignature;
